/**
 * CRM Products — product master list (B端产品) with create/edit/delete.
 *
 * Pricing is reference-only here; quoting is case-by-case on each deal line.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SearchInput,
  Select,
  Badge,
  EmptyState,
  Button,
  SkeletonRow,
  ConfirmDialog,
  Dialog,
  Input,
  Textarea,
  Spinner,
  toast,
  Pagination,
} from '../../components/ui';
import { Package, Plus, Edit3, Trash2 } from '../../lib/icons';
import { useT } from '../../lib/i18n';
import { usePersistedPageSize } from '../../hooks/use-persisted-page-size';
import { fetchProducts, createProduct, updateProduct, deleteProduct } from '../../lib/crm-api';
import type { CrmProduct } from '../../lib/crm-api';

export function ProductsPage() {
  const t = useT();
  const [products, setProducts] = useState<CrmProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [active, setActive] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = usePersistedPageSize('crm.products', 20);

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CrmProduct | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CrmProduct | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    timerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchProducts({
        search: debouncedSearch || undefined,
        is_active: active === '' ? undefined : active === 'true',
        limit: pageSize,
        offset: page * pageSize,
      });
      setProducts(res.items);
      setTotal(res.total);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, active, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, active, pageSize]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProduct(deleteTarget.id);
      toast(t('crm.productDeleted'), 'success');
      setDeleteTarget(null);
      loadData();
    } catch {
      toast(t('crm.deleteFailed'), 'error');
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Filter bar */}
      <div className="border-b border-edge bg-surface-raised px-4 py-2.5 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput
            size="sm"
            className="flex-1 min-w-[120px] sm:flex-none sm:w-[220px]"
            value={search}
            onChange={setSearch}
            placeholder={t('crm.searchProducts')}
          />
          <Select size="sm" inline value={active} onChange={(e) => setActive(e.target.value)}>
            <option value="">—</option>
            <option value="true">{t('crm.productActive')}</option>
            <option value="false">{t('crm.productInactive')}</option>
          </Select>
          <div className="flex-1" />
          <span className="text-xs text-fg-faint">{total} results</span>
          <Button
            size="sm"
            onClick={() => {
              setEditTarget(null);
              setFormOpen(true);
            }}
          >
            <Plus size={14} />
            <span className="hidden sm:inline ml-1">{t('crm.addProduct')}</span>
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {!loading && products.length === 0 ? (
          <EmptyState
            icon={Package}
            title={t('crm.noProductsFound')}
            description={debouncedSearch ? t('crm.adjustSearch') : t('crm.createFirstProduct')}
          />
        ) : (
          <table className="w-full min-w-[760px] [&_td]:whitespace-nowrap">
            <thead className="sticky top-0 z-10 bg-surface-muted [&_th]:whitespace-nowrap">
              <tr className="border-b border-edge bg-surface-muted">
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2">{t('crm.productNo')}</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2">{t('crm.productName')}</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden md:table-cell">
                  {t('crm.category')}
                </th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden lg:table-cell">
                  {t('crm.brand')}
                </th>
                <th className="text-right text-xs font-medium text-fg-muted px-4 py-2">{t('crm.unitPrice')}</th>
                <th className="text-right text-xs font-medium text-fg-muted px-4 py-2 hidden lg:table-cell">
                  {t('crm.netWeight')}
                </th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden md:table-cell">
                  {t('crm.hsCode')}
                </th>
                <th className="w-16" />
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={8} />)
                : products.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => {
                        setEditTarget(p);
                        setFormOpen(true);
                      }}
                      className="border-b border-edge hover:bg-surface-muted cursor-pointer transition-colors group"
                    >
                      <td className="px-4 py-2.5">
                        <span className="text-xs font-mono text-fg-muted">{p.product_no || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-md bg-surface-muted flex items-center justify-center flex-shrink-0">
                            <Package size={13} className="text-fg-muted" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-fg truncate" title={p.name}>
                              {p.name}
                            </div>
                            {p.name_cn && (
                              <div className="text-[11px] text-fg-faint truncate" title={p.name_cn}>
                                {p.name_cn}
                              </div>
                            )}
                          </div>
                          {!p.is_active && <Badge variant="secondary">{t('crm.productInactive')}</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell">
                        <span className="text-xs text-fg-secondary">{p.category || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5 hidden lg:table-cell">
                        <span className="text-xs text-fg-secondary">{p.brand || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="text-xs text-fg-secondary">
                          {p.default_unit_price != null ? `$${p.default_unit_price.toLocaleString()}` : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right hidden lg:table-cell">
                        <span className="text-xs text-fg-secondary">
                          {p.net_weight_kg != null ? `${p.net_weight_kg} kg` : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell">
                        <span className="text-[11px] font-mono text-fg-muted">{p.hs_code || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 touch-visible transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditTarget(p);
                              setFormOpen(true);
                            }}
                            className="p-1 rounded hover:bg-surface-muted text-fg-faint hover:text-fg-secondary"
                            title={t('common.edit')}
                          >
                            <Edit3 size={13} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(p);
                            }}
                            className="p-1 rounded hover:bg-danger-subtle text-fg-faint hover:text-danger"
                            title={t('common.delete')}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />

      <ProductFormDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={loadData} initial={editTarget} />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('crm.deleteProduct')}
        description={`Delete "${deleteTarget?.name}"?`}
        confirmLabel="Delete"
        confirmVariant="destructive"
      />
    </div>
  );
}

// ─── Product Form Dialog ─────────────────────────────────

interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initial?: CrmProduct | null;
}

const EMPTY = {
  product_no: '',
  name: '',
  name_cn: '',
  name_en: '',
  brand: '',
  category: '',
  default_unit_price: '',
  hs_code: '',
  length_cm: '',
  width_cm: '',
  height_cm: '',
  net_weight_kg: '',
  carton_capacity: '',
  default_remark: '',
  notes: '',
  is_active: true,
};

function ProductFormDialog({ open, onClose, onSaved, initial }: ProductFormProps) {
  const t = useT();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      initial
        ? {
            product_no: initial.product_no || '',
            name: initial.name,
            name_cn: initial.name_cn || '',
            name_en: initial.name_en || '',
            brand: initial.brand || '',
            category: initial.category || '',
            default_unit_price: initial.default_unit_price != null ? String(initial.default_unit_price) : '',
            hs_code: initial.hs_code || '',
            length_cm: initial.length_cm != null ? String(initial.length_cm) : '',
            width_cm: initial.width_cm != null ? String(initial.width_cm) : '',
            height_cm: initial.height_cm != null ? String(initial.height_cm) : '',
            net_weight_kg: initial.net_weight_kg != null ? String(initial.net_weight_kg) : '',
            carton_capacity: initial.carton_capacity != null ? String(initial.carton_capacity) : '',
            default_remark: initial.default_remark || '',
            notes: initial.notes || '',
            is_active: initial.is_active,
          }
        : EMPTY,
    );
  }, [open, initial]);

  const set = (k: string, v: string | boolean) => setForm((p) => ({ ...p, [k]: v }));
  const numOrUndef = (v: string) => (v.trim() === '' ? undefined : Number(v));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast(t('crm.nameRequired'), 'error');
      return;
    }
    setSaving(true);
    const payload = {
      product_no: form.product_no || undefined,
      name: form.name.trim(),
      name_cn: form.name_cn || undefined,
      name_en: form.name_en || undefined,
      brand: form.brand || undefined,
      category: form.category || undefined,
      default_unit_price: numOrUndef(form.default_unit_price),
      hs_code: form.hs_code || undefined,
      length_cm: numOrUndef(form.length_cm),
      width_cm: numOrUndef(form.width_cm),
      height_cm: numOrUndef(form.height_cm),
      net_weight_kg: numOrUndef(form.net_weight_kg),
      carton_capacity: numOrUndef(form.carton_capacity),
      default_remark: form.default_remark || undefined,
      notes: form.notes || undefined,
      is_active: form.is_active,
    };
    try {
      if (initial) {
        await updateProduct(initial.id, payload);
        toast(t('crm.productUpdated'), 'success');
      } else {
        await createProduct(payload);
        toast(t('crm.productCreated'), 'success');
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      toast((err as Error).message || t('crm.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={initial ? t('crm.productName') : t('crm.addProduct')} size="lg">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('crm.productName') + ' *'}>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="LPH-MAX" />
          </Field>
          <Field label={t('crm.productNo')}>
            <Input value={form.product_no} onChange={(e) => set('product_no', e.target.value)} placeholder="BPD00001" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('crm.nameCn')}>
            <Input value={form.name_cn} onChange={(e) => set('name_cn', e.target.value)} />
          </Field>
          <Field label={t('crm.nameEn')}>
            <Input value={form.name_en} onChange={(e) => set('name_en', e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t('crm.brand')}>
            <Input value={form.brand} onChange={(e) => set('brand', e.target.value)} />
          </Field>
          <Field label={t('crm.category')}>
            <Input value={form.category} onChange={(e) => set('category', e.target.value)} />
          </Field>
          <Field label={t('crm.hsCode')}>
            <Input value={form.hs_code} onChange={(e) => set('hs_code', e.target.value)} />
          </Field>
        </div>
        <Field label={`${t('crm.unitPrice')} (USD)`} hint={t('crm.referencePriceNote')}>
          <Input
            type="number"
            value={form.default_unit_price}
            onChange={(e) => set('default_unit_price', e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-4 gap-3">
          <Field label="L (cm)">
            <Input type="number" value={form.length_cm} onChange={(e) => set('length_cm', e.target.value)} />
          </Field>
          <Field label="W (cm)">
            <Input type="number" value={form.width_cm} onChange={(e) => set('width_cm', e.target.value)} />
          </Field>
          <Field label="H (cm)">
            <Input type="number" value={form.height_cm} onChange={(e) => set('height_cm', e.target.value)} />
          </Field>
          <Field label={`${t('crm.netWeight')} (kg)`}>
            <Input type="number" value={form.net_weight_kg} onChange={(e) => set('net_weight_kg', e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('crm.cartonCapacity')}>
            <Input
              type="number"
              value={form.carton_capacity}
              onChange={(e) => set('carton_capacity', e.target.value)}
            />
          </Field>
          <Field label={t('crm.productActive')}>
            <Select
              value={form.is_active ? 'true' : 'false'}
              onChange={(e) => set('is_active', e.target.value === 'true')}
            >
              <option value="true">{t('crm.productActive')}</option>
              <option value="false">{t('crm.productInactive')}</option>
            </Select>
          </Field>
        </div>
        <Field label={t('crm.notes')}>
          <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Spinner className="h-3 w-3 mr-1" />}
            {initial ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-fg-muted mb-1 block">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-fg-faint mt-1">{hint}</p>}
    </div>
  );
}
